import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';

import { useKeyboardNav } from '../../hooks/useKeyboardNav';
import { cn } from '../../styles/lib/utils';
import { Icon } from '../Icon';

// cmdk sets aria-activedescendant before its options re-render, leaving it stale; this follows data-selected.
function useActiveDescendantSync(rootRef: React.RefObject<HTMLDivElement | null>) {
  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const sync = () => {
      const input = root.querySelector<HTMLInputElement>('[cmdk-input]');
      if (!input) return;
      const active = root.querySelector<HTMLElement>('[cmdk-item][data-selected="true"]');
      const id = active && !active.closest('[hidden]') ? active.id : '';
      if (id && input.getAttribute('aria-activedescendant') !== id) input.setAttribute('aria-activedescendant', id);
      if (!id && input.hasAttribute('aria-activedescendant')) input.removeAttribute('aria-activedescendant');
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-selected', 'aria-activedescendant', 'hidden'],
    });
    return () => observer.disconnect();
  }, [rootRef]);
}

function Command({ className, ref, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const composedRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      rootRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.RefObject<HTMLDivElement | null>).current = el;
    },
    [ref],
  );
  useActiveDescendantSync(rootRef);
  const keyboardNav = useKeyboardNav();

  return (
    <CommandPrimitive
      ref={composedRef}
      data-keyboard-nav={keyboardNav || undefined}
      data-slot="command"
      className={cn('bg-white text-primary flex h-full w-full flex-col overflow-hidden', className)}
      {...props}
    />
  );
}

function CommandInput({
  className,
  borderColor = 'border-grey-30',
  wrapperClassName,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input> & { borderColor?: string; wrapperClassName?: string }) {
  return (
    <div
      data-slot="command-input-wrapper"
      className={cn('relative flex h-7 items-center border-b focus-within:border-focus', borderColor, wrapperClassName)}
    >
      <div className="absolute left-2 pointer-events-none z-10">
        <Icon icon="magnifyingGlass" size={14} className="text-grey-50" />
      </div>
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          'flex h-full w-full bg-transparent pl-7 pr-2 text-xs outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          'placeholder:text-grey-50',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        'max-h-[300px] scroll-py-1 overflow-y-auto overflow-x-hidden',
        '[&::-webkit-scrollbar]:w-2.5',
        '[&::-webkit-scrollbar-thumb]:bg-grey-30 [&::-webkit-scrollbar-thumb:hover]:bg-grey-50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-white',
        '[&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track]:border [&::-webkit-scrollbar-track]:border-white',
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({ ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return <CommandPrimitive.Empty data-slot="command-empty" className="py-6 text-center text-sm" {...props} />;
}

function CommandGroup({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'text-primary p-1',
        '[&_[cmdk-group-heading]]:text-grey-50 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn('bg-grey-30 -mx-1 h-px', className)}
      {...props}
    />
  );
}

function CommandItem({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        'text-xs font-noto text-grey-90 font-normal relative flex cursor-default items-center gap-2 px-2 h-7 outline-hidden select-none',
        'data-[selected=true]:bg-grey-10 data-[selected=true]:text-primary-dark',
        // Focus stays in the input, so the highlighted option carries the ring, and only while the keyboard is in use.
        'in-data-[keyboard-nav=true]:data-[selected=true]:highlight-ring',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0',
        "[&_svg:not([class*='text-'])]:text-grey-50",
        "[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn('ml-auto text-xs text-grey-50 tracking-widest', className)}
      {...props}
    />
  );
}

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
